// ask_user (WEB-7134, API-3795): a reply that ends with questions only the user can answer, such as
// the state, species or residency. The questions come as the step Result
//   { Kind: 'ask_user', Questions: [{ Id, Question, Options: [{ Label }], MultiSelect, AllowOther }] },
// which a saved chat keeps, so a reopened chat can still be answered. The answer goes back as an
// ordinary next question, written from the picks.

const text = v => (typeof v === 'string' ? v.trim() : '')

// The step Result is PascalCase (Question, Options[].Label, MultiSelect, AllowOther) while the action
// Input is snake_case (question, options[].label, multi_select, allow_other); either is read.
const pick = (o, pascal, snake) => (o[pascal] !== undefined ? o[pascal] : o[snake])

const normalizeQuestion = (q, i) => {
  if (!q || typeof q !== 'object') return null
  const question = text(pick(q, 'Question', 'question'))
  if (!question) return null
  const seen = new Set()
  const rawOptions = pick(q, 'Options', 'options')
  const options = (Array.isArray(rawOptions) ? rawOptions : [])
    .map(o => text(o && typeof o === 'object' ? pick(o, 'Label', 'label') : o))
    .filter(label => label && !seen.has(label) && seen.add(label))
  return {
    id: text(pick(q, 'Id', 'id')) || `q${i}`,
    question,
    options,
    multiSelect: pick(q, 'MultiSelect', 'multi_select') === true,
    // Without options the user has to type, whatever allow_other says.
    allowOther: pick(q, 'AllowOther', 'allow_other') !== false || options.length === 0
  }
}

// The questions the reply asked, [] when it asked none. The last ask_user step wins.
export const askQuestions = message => {
  const steps = (message && message.steps) || []
  for (let i = steps.length - 1; i >= 0; i--) {
    const result = steps[i].result
    if (result && result.Kind === 'ask_user' && Array.isArray(result.Questions)) {
      return result.Questions.map(normalizeQuestion).filter(Boolean)
    }
  }
  return []
}

// A question that waits for an answer: the latest reply, finished, asking.
export const isAsking = (message, latest) =>
  !!latest && !!message && message.status === 'complete' && askQuestions(message).length > 0

// One single-choice question with choices: a tap answers it, with no Send button.
export const sendsOnTap = questions =>
  questions.length === 1 && questions[0].options.length > 0 && !questions[0].multiSelect

// picks: { [question id]: [labels] }. Single choice replaces the pick, tapping it again clears it.
export const togglePick = (picks, question, label) => {
  const current = picks[question.id] || []
  const next = current.includes(label)
    ? current.filter(l => l !== label)
    : question.multiSelect ? [...current, label] : [label]
  return { ...picks, [question.id]: next }
}

// What the user typed wins over what they tapped.
export const answerFor = (question, picks = {}, other = {}) =>
  text(other[question.id]) || (picks[question.id] || []).join(', ')

export const allAnswered = (questions, picks, other) =>
  questions.length > 0 && questions.every(q => answerFor(q, picks, other))

// The message sent back: "Colorado" for one answer, "Colorado. Resident." for several.
export const composeAnswer = (questions, picks, other) => {
  const answers = questions.map(q => answerFor(q, picks, other).replace(/[.\s]+$/, '')).filter(Boolean)
  return answers.length > 1 ? `${answers.join('. ')}.` : answers.join('')
}

// The reply's text without the questions the server appended for clients without the card
// (<p>Question</p> each): what Artemis wrote before asking, or '' when that's all there was.
const decode = s => s.replace(/<[^>]+>/g, '').replace(/&quot;/g, '"').replace(/&#39;|&#x27;/g, "'")
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').trim()
export const leadIn = (html, questions) => {
  if (!html) return ''
  const asked = new Set((questions || []).map(q => q.question))
  return html.replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, (p, inner) => (asked.has(decode(inner)) ? '' : p)).trim()
}
